// Speak — the talk-it-through session (Roadmap ④; Design Brief screen 9; brain: docs/DIALOGUE_BRAIN.md).
//
// /speak/new        — minimal frame (class · week · topic · duration) → creates the lesson → /speak/:id
// /speak/:lessonId  — consent gate (first use) → the dialogue surface → the read-only summary card.
//
// Rails carried by this page:
//  - The recording banner stays visible the WHOLE time audio is captured (RECORDING_NOTICE §1).
//  - No correction UI of any kind: the coach's words appear as calm text; the teacher's English
//    is never marked, scored, or metered. Recasts are invisible by design.
//  - The summary card is READ-ONLY. There is no copy-into-plan affordance anywhere — the teacher
//    reads it, then TYPES the plan (path='dialogue'). Auto-fill is the one sin this page can't commit.
//
// Transport (2026-07-01 iOS-safe rebuild): ticket from /api/dialogue/ticket → DIRECT HTTPS to the
// Dialogue Worker (the wall — coaching bytes never touch the Pages app). Turn-based, not a socket:
// each turn records one clip with MediaRecorder, decodes it to a base64 WAV client-side, and POSTs
// it to /turn; the coach's text + voice (base64 WAV) come back. Voice plays through one unlockable
// <audio> element. This replaced the AudioWorklet/raw-PCM + Web-Audio path that failed on iPhone
// (empty audio in a home-screen PWA; blocked autoplay). A failed turn never kills the session — the
// teacher just retries. See the HTTP-surface contract at the top of workers/dialogue/index.ts.

import { api, ApiError } from "../api";
import { esc } from "../dom";
import { navigate } from "../router";
import { t } from "../i18n";
import { startRecording, type Recorder } from "../audio/capture";
import { Playback } from "../audio/playback";

interface Cls {
  id: string;
  name: string;
  level: string | null;
}
interface TermInfo {
  term: { id: string };
  weeks: number[];
  current_week: number;
}
interface LessonMeta {
  lesson: { id: string; topic: string; class_name: string; week_no: number };
}
interface TicketResponse {
  ticket: string;
  dialogue_url: string;
}
interface SessionCard {
  id: string;
  ended_at: string | null;
  decisions: Record<string, string>;
  summary: { insights: string[]; key_points: string[] } | null;
  corrected_script: string | null;
  shared_with_lead: boolean;
}

const CELLS = ["prior", "misconception", "check"] as const;
const cellLabel = (c: string) =>
  c === "prior" ? t("speak.cell_prior") : c === "misconception" ? t("speak.cell_misconception") : t("speak.cell_check");

// ── /speak/new — the frame form ─────────────────────────────────────────────────────────────
export async function renderSpeakNew(app: HTMLElement): Promise<void> {
  const [{ classes }, term] = await Promise.all([
    api<{ classes: Cls[] }>("/api/classes?scope=own"),
    api<TermInfo>("/api/terms/current"),
  ]);

  if (classes.length === 0) {
    app.innerHTML = `
      <div class="topbar"><a data-link href="/plan/start" class="pill-btn">${t("common.back")}</a></div>
      <h1>${t("speak.title")}</h1>
      <p class="empty">${t("plan.no_class")}</p>
      <a class="btn" data-link href="/classes">${t("plan.add_class")}</a>`;
    return;
  }

  const classOpts = classes
    .map((c) => `<option value="${esc(c.id)}">${esc(c.name)}${c.level ? ` · ${esc(c.level)}` : ""}</option>`)
    .join("");
  const weekOpts = term.weeks
    .map((w) => `<option value="${w}"${w === term.current_week ? " selected" : ""}>${t("common.week_n", { n: w })}</option>`)
    .join("");

  app.innerHTML = `
    <div class="topbar"><a data-link href="/plan/start" class="pill-btn">${t("common.back")}</a></div>
    <h1>${t("speak.title")}</h1>
    <p class="hint">${t("speak.frame_hint")}</p>

    <label for="class">${t("plan.class")}</label>
    <select id="class">${classOpts}</select>

    <label for="week">${t("plan.week")}</label>
    <select id="week">${weekOpts}</select>

    <label for="topic">${t("plan.topic")}</label>
    <input id="topic" placeholder="${t("plan.topic_placeholder")}" required />

    <label for="duration">${t("plan.duration")}</label>
    <input id="duration" type="number" value="60" min="5" step="5" />

    <div id="err" class="error" hidden></div>
    <div style="height:16px"></div>
    <button class="btn moss" id="start" type="button">${t("speak.start_button")}</button>`;

  const val = (id: string): string => (app.querySelector(id) as HTMLInputElement | HTMLSelectElement | null)?.value ?? "";
  const err = app.querySelector<HTMLElement>("#err")!;

  app.querySelector<HTMLButtonElement>("#start")!.addEventListener("click", async () => {
    err.hidden = true;
    const topic = val("#topic").trim();
    if (!topic) {
      err.textContent = t("plan.topic_missing");
      err.hidden = false;
      return;
    }
    try {
      const lesson = await api<{ id: string }>("/api/lessons", {
        method: "POST",
        body: JSON.stringify({
          class_id: val("#class"),
          term_id: term.term.id,
          week_no: Number(val("#week")),
          topic,
          duration_min: Number(val("#duration")) || 60,
        }),
      });
      navigate(`/speak/${lesson.id}`);
    } catch (ex) {
      err.textContent = (ex as Error).message;
      err.hidden = false;
    }
  });
}

// ── /speak/:lessonId — consent gate → session → summary card ───────────────────────────────
export async function renderSpeak(app: HTMLElement, params: Record<string, string>): Promise<void> {
  const lessonId = params.id!;

  const consent = await api<{ consented_at: string | null }>("/api/me/dialogue-consent");
  if (!consent.consented_at) {
    renderConsent(app, lessonId);
    return;
  }
  await renderSession(app, lessonId);
}

// First-use consent (RECORDING_NOTICE §2) — shown before the FIRST spoken session only;
// fast-capture owes no notice (typing is never recorded).
function renderConsent(app: HTMLElement, lessonId: string): void {
  const bullets = ["consent.what_saved", "consent.who", "consent.for", "consent.lead_listens", "consent.where", "consent.typing"]
    .map((k) => `<li style="margin-bottom:12px">${t(k as Parameters<typeof t>[0])}</li>`)
    .join("");
  app.innerHTML = `
    <div class="topbar"><a data-link href="/plan/start" class="pill-btn">${t("common.back")}</a></div>
    <h1>${t("consent.title")}</h1>
    <p>${t("consent.intro")}</p>
    <ul style="padding-left:20px">${bullets}</ul>
    <p class="hint">${t("consent.agree")}</p>
    <div id="err" class="error" hidden></div>
    <div style="height:8px"></div>
    <button class="btn moss" id="agree" type="button">${t("consent.start")}</button>
    <div style="height:8px"></div>
    <button class="btn secondary" id="type" type="button">${t("consent.type_instead")}</button>`;

  const err = app.querySelector<HTMLElement>("#err")!;
  app.querySelector<HTMLButtonElement>("#agree")!.addEventListener("click", async () => {
    try {
      await api("/api/me/dialogue-consent", { method: "POST" });
      await renderSession(app, lessonId);
    } catch (ex) {
      err.textContent = (ex as Error).message;
      err.hidden = false;
    }
  });
  app.querySelector<HTMLButtonElement>("#type")!.addEventListener("click", () => navigate(`/lesson/${lessonId}/edit`));
}

async function mintTicket(lessonId: string): Promise<TicketResponse> {
  return api<TicketResponse>("/api/dialogue/ticket", { method: "POST", body: JSON.stringify({ lesson_id: lessonId }) });
}

// ── turn-based session helpers ────────────────────────────────────────────────────────────────
interface TurnResponse {
  session_id?: string;
  teacherText?: string;
  coachText?: string;
  coachAudioB64?: string | null;
  decisions?: Record<string, string>;
  complete?: boolean;
  summary?: SessionCard["summary"];
  corrected_script?: string | null;
  error?: "no_keys" | "quota" | "failed" | string;
}
// iOS home-screen PWAs hit a WebKit bug where getUserMedia yields empty audio; detect that combo so
// we can point the teacher to Safari (bugs.webkit.org #185448 / #208667).
function isIosStandalone(): boolean {
  const nav = navigator as unknown as { standalone?: boolean };
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const standalone = nav.standalone === true || window.matchMedia?.("(display-mode: standalone)").matches === true;
  return isIos && standalone;
}

async function renderSession(app: HTMLElement, lessonId: string): Promise<void> {
  // No key → no session AT ALL. The pointer to Settings is the whole screen — starting a
  // session that can only fail would bury this message under an error (the bug the pilot hit).
  const { keys } = await api<{ keys: { status: string }[] }>("/api/me/keys");
  const hasKey = keys.some((k) => k.status !== "disabled");
  if (!hasKey) {
    app.innerHTML = `
      <div class="topbar"><a data-link href="/plan/start" class="pill-btn">${t("common.back")}</a></div>
      <h1>${t("speak.no_keys_title")}</h1>
      <p>${t("speak.no_keys")}</p>
      <a class="btn" data-link href="/settings">${t("speak.no_keys_link")}</a>
      <div style="height:8px"></div>
      <button class="btn secondary" id="type" type="button">${t("consent.type_instead")}</button>`;
    app.querySelector<HTMLButtonElement>("#type")!.addEventListener("click", () => navigate(`/lesson/${lessonId}/edit`));
    return;
  }

  const meta = await api<LessonMeta>(`/api/lessons/${encodeURIComponent(lessonId)}`);

  let tk: TicketResponse;
  try {
    tk = await mintTicket(lessonId);
  } catch (ex) {
    if (ex instanceof ApiError && ex.status === 409) {
      renderConsent(app, lessonId); // consent got out of sync — show the notice, not an error
      return;
    }
    throw ex;
  }

  // A finished session WITH CONTENT already exists → show its card (talking again stays one
  // tap away). Content-free stubs (historical failed starts) are ignored, never a card.
  const existing = await fetchCard(tk, lessonId);
  if (
    existing?.ended_at &&
    (existing.summary || existing.corrected_script || Object.keys(existing.decisions ?? {}).length > 0)
  ) {
    renderCard(app, lessonId, existing, true);
    return;
  }

  app.innerHTML = `
    <div class="banner-recording" id="recbanner" hidden><span class="rec-dot"></span><span>${t("speak.banner")}</span></div>
    <div class="topbar"><a data-link href="/this-week" class="pill-btn" id="leave">${t("common.back_this_week")}</a></div>
    <h1>${t("speak.title")}</h1>
    <p class="muted">${esc(meta.lesson.class_name)} · ${esc(meta.lesson.topic)} · ${t("common.week_n", { n: meta.lesson.week_no })}</p>

    <div class="turns">
      <div class="coach-turn">
        <div class="coach-name">EDEN</div>
        <p id="coachline" class="coach-text">${t("speak.connecting")}</p>
      </div>
    </div>

    <div id="cells"></div>

    <div id="err" class="error" hidden></div>
    <div style="height:8px"></div>
    <button class="btn secondary" id="retry" type="button" hidden>${t("speak.try_again")}</button>
    <div style="height:8px"></div>
    <button class="btn moss" id="mic" type="button" hidden>${t("speak.tap_to_start")}</button>

    <div id="controls" hidden>
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <button class="btn secondary" id="help" type="button">${t("speak.more_help")}</button>
        <button class="btn secondary" id="texttoggle" type="button">${t("speak.text_toggle")}</button>
        <button class="btn" id="end" type="button">${t("speak.end")}</button>
      </div>
      <form id="textform" hidden style="margin-top:8px">
        <input id="textinput" placeholder="${t("speak.text_placeholder")}" autocomplete="off" />
        <div style="height:8px"></div>
        <button class="btn secondary" type="submit">${t("speak.send")}</button>
      </form>
    </div>

    <div class="escape-typing"><button type="button" id="escape">${t("speak.escape_typing")}</button></div>`;

  const coachline = app.querySelector<HTMLElement>("#coachline")!;
  const cellsBox = app.querySelector<HTMLElement>("#cells")!;
  const banner = app.querySelector<HTMLElement>("#recbanner")!;
  const err = app.querySelector<HTMLElement>("#err")!;
  const micBtn = app.querySelector<HTMLButtonElement>("#mic")!;
  const retryBtn = app.querySelector<HTMLButtonElement>("#retry")!;
  const controls = app.querySelector<HTMLElement>("#controls")!;
  const helpBtn = app.querySelector<HTMLButtonElement>("#help")!;

  // Hard failure (the session never started): the error STAYS readable + a calm full retry.
  const showFailure = (message: string) => {
    err.textContent = message;
    err.hidden = false;
    retryBtn.hidden = false;
    micBtn.hidden = true;
    controls.hidden = true;
    banner.hidden = true;
  };
  retryBtn.addEventListener("click", () => void renderSession(app, lessonId));

  const decisions: Record<string, string> = {};
  const renderCells = () => {
    const chips = CELLS.filter((c) => decisions[c]);
    cellsBox.innerHTML = chips.length
      ? `<div class="decided">
           <div class="decided-title">${t("speak.you_decided")}</div>
           ${chips
             .map(
               (c) =>
                 `<div class="decided-chip"><span class="cell">${cellLabel(c)}</span><span class="what">${esc(decisions[c]!)}</span></div>`,
             )
             .join("")}
         </div>`
      : "";
  };

  const playback = new Playback();
  let recorder: Recorder | null = null; // the live MediaRecorder for the open turn (null between turns)
  let sessionId: string | null = null;
  let turnActive = false; // a turn is open (button shows "Done")
  let busy = false; // a /turn or /start request is in flight — ignore taps
  let helpUp = false; // sticky dial-up: once "More help" is tapped, every turn re-sends it
  let left = false; // the page was torn down — drop any late responses

  // Mobile browsers suspend the AudioContext when the tab backgrounds (teacher glances at a message),
  // which freezes mic capture and coach playback. Resume both the moment the page is visible again.
  const onVisible = () => {
    if (document.visibilityState === "visible") playback.resume();
  };

  const cleanup = () => {
    left = true;
    document.removeEventListener("visibilitychange", onVisible);
    recorder?.cancel();
    recorder = null;
    playback.close();
    banner.hidden = true;
  };
  // The router has no unmount hook: leaving the page must still kill the mic
  // (the red recording light must never outlive the session — honesty rail).
  window.addEventListener("pagehide", cleanup, { once: true });
  window.addEventListener("popstate", cleanup, { once: true });
  document.addEventListener("visibilitychange", onVisible);
  app.querySelector<HTMLAnchorElement>("#leave")!.addEventListener("click", cleanup);
  // Always-visible escape: switch to typing. Kill the mic first (the recording light must never
  // outlive the session — honesty rail), then open the fast-capture form.
  app.querySelector<HTMLButtonElement>("#escape")!.addEventListener("click", () => {
    cleanup();
    navigate("/plan/new");
  });

  // One POST to the Dialogue Worker, ticket on the URL. A 30 s timeout means a hung network never
  // freezes the screen; a 401 (the session ticket aged out) transparently re-mints once.
  const post = async (path: string, payload: Record<string, unknown>): Promise<TurnResponse> => {
    const send = async (): Promise<Response> => {
      const ctrl = new AbortController();
      const timer = window.setTimeout(() => ctrl.abort(), 30_000);
      try {
        return await fetch(`${tk.dialogue_url}${path}?ticket=${encodeURIComponent(tk.ticket)}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: ctrl.signal,
        });
      } finally {
        clearTimeout(timer);
      }
    };
    try {
      let res = await send();
      if (res.status === 401) {
        tk = await mintTicket(lessonId); // session ticket aged out mid-conversation — refresh + retry
        res = await send();
      }
      return (await res.json()) as TurnResponse;
    } catch {
      return { error: "failed" };
    }
  };

  const playCoach = (b64: string | null | undefined) => {
    playback.play(b64);
  };
  const showCoach = (text: string) => {
    coachline.textContent = text;
    coachline.classList.remove("muted");
  };
  const setMicLabel = () => {
    micBtn.textContent = turnActive ? t("speak.done") : t("speak.tap_to_talk");
  };

  // Apply a coach reply (from /start or /turn). On `complete`, end the session and show the card.
  const applyReply = (r: TurnResponse) => {
    if (r.decisions) {
      for (const [k, v] of Object.entries(r.decisions)) decisions[k] = v;
      renderCells();
    }
    if (r.coachText) showCoach(r.coachText);
    else if (!r.complete) showCoach(t("speak.tap_to_start")); // tool-only turn: clear "Thinking…"
    playCoach(r.coachAudioB64);
    micBtn.hidden = false;
    setMicLabel();
    if (r.complete) void finishSession();
  };

  // Soft, per-turn failure: the session stays alive. Re-enable the mic so the next tap retries.
  const showTurnRetry = (reason?: string) => {
    coachline.textContent = reason === "quota" ? t("speak.resting") : reason === "no_keys" ? t("speak.no_keys") : t("speak.turn_failed");
    coachline.classList.add("muted");
    micBtn.hidden = false;
    setMicLabel();
  };

  const startTurn = async () => {
    if (busy || turnActive) return;
    playback.flush(); // stop the coach if it's still speaking (the teacher wants to talk)
    controls.hidden = false;
    err.hidden = true;
    try {
      recorder = await startRecording(); // opens the mic for THIS turn only
    } catch {
      // mic blocked/unavailable — distinct from a quota rest. Point at the typing fallback.
      err.textContent = isIosStandalone()
        ? "On iPhone, open eden in Safari (not the Home-Screen app) to use the microphone — or use Type instead."
        : t("speak.hard_fail");
      err.hidden = false;
      banner.hidden = true;
      return;
    }
    banner.hidden = false; // the mic is live → the recording notice stays visible (notice §1)
    turnActive = true;
    setMicLabel();
    coachline.textContent = t("speak.listening");
    coachline.classList.remove("muted");
  };

  const endTurn = async () => {
    if (!turnActive || busy) return;
    turnActive = false;
    busy = true;
    setMicLabel();
    banner.hidden = true; // mic released the instant the turn ends (the red light must not linger)
    coachline.textContent = t("speak.thinking");
    coachline.classList.add("muted");

    const rec = recorder;
    recorder = null;
    const captured = rec ? await rec.stop() : null; // stop, release the mic, get the WAV
    if (!captured) {
      // Nothing usable recorded (silence, or the iOS home-screen-PWA mic bug). Keep the session
      // alive and steer to the reliable path — never a dead end.
      busy = false;
      if (left) return;
      coachline.textContent = t("speak.turn_failed");
      coachline.classList.add("muted");
      if (isIosStandalone()) {
        err.textContent = "On iPhone, open eden in Safari (not the Home-Screen app) for the microphone — or tap Type instead.";
        err.hidden = false;
      }
      micBtn.hidden = false;
      setMicLabel();
      return;
    }

    const r = await post("/turn", { session_id: sessionId, audio: captured.wavB64, help_up: helpUp });
    busy = false;
    if (left) return;
    if (r.error) {
      showTurnRetry(r.error);
      return;
    }
    applyReply(r);
  };

  micBtn.addEventListener("click", () => {
    if (!sessionId) void beginSession(); // first tap: unlock audio + fetch the opener (iOS autoplay)
    else if (turnActive) void endTurn();
    else void startTurn();
  });

  helpBtn.addEventListener("click", () => {
    helpUp = true; // sticky for the rest of the session; the next /turn carries it
    helpBtn.disabled = true;
    helpBtn.textContent = t("speak.help_on");
  });

  const textform = app.querySelector<HTMLFormElement>("#textform")!;
  app.querySelector<HTMLButtonElement>("#texttoggle")!.addEventListener("click", () => {
    textform.hidden = !textform.hidden;
  });
  textform.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (busy) return;
    const input = app.querySelector<HTMLInputElement>("#textinput")!;
    const text = input.value.trim();
    if (!text) return;
    input.value = "";
    busy = true;
    coachline.textContent = t("speak.thinking");
    coachline.classList.add("muted");
    const r = await post("/turn", { session_id: sessionId, text, help_up: helpUp });
    busy = false;
    if (left) return;
    if (r.error) showTurnRetry(r.error);
    else applyReply(r);
  });

  async function finishSession(): Promise<void> {
    if (!sessionId) return;
    const r = await post("/session/end", { session_id: sessionId });
    cleanup();
    if (left && r.error) return;
    const card: SessionCard = {
      id: r.session_id ?? sessionId,
      ended_at: "now",
      decisions: r.decisions ?? decisions,
      summary: r.summary ?? null,
      corrected_script: r.corrected_script ?? null,
      shared_with_lead: false,
    };
    renderCard(app, lessonId, card, false);
  }
  app.querySelector<HTMLButtonElement>("#end")!.addEventListener("click", () => {
    if (!busy) void finishSession();
  });

  // ── Kick off ────────────────────────────────────────────────────────────────────────────────
  // iOS only plays audio that starts from a user gesture, and the coach's voice arrives AFTER an
  // await (STT+CHAT+TTS). So we gate the opener behind the first tap: that tap unlocks the <audio>
  // element (playback.unlock) and fetches the opener; the coach's voice then plays fine.
  async function beginSession(): Promise<void> {
    if (busy) return;
    busy = true;
    playback.unlock(); // MUST run inside this tap — enables later programmatic play() on iOS
    micBtn.disabled = true;
    err.hidden = true;
    coachline.textContent = t("speak.connecting");
    coachline.classList.add("muted");
    const start = await post("/session/start", {});
    busy = false;
    micBtn.disabled = false;
    if (left) return;
    if (start.error || !start.session_id) {
      showFailure(start.error === "no_keys" ? t("speak.no_keys") : start.error === "quota" ? t("speak.resting") : t("speak.hard_fail"));
      return;
    }
    sessionId = start.session_id;
    showCoach(start.coachText ?? t("speak.tap_to_start"));
    playCoach(start.coachAudioB64);
    setMicLabel();
  }

  // Initial state: the coach is ready and the mic invites the first tap (no audio yet — iOS gesture).
  showCoach(t("speak.tap_to_start"));
  micBtn.hidden = false;
  setMicLabel();
}

async function fetchCard(tk: TicketResponse, lessonId: string): Promise<SessionCard | null> {
  try {
    const res = await fetch(`${tk.dialogue_url}/session/by-lesson/${encodeURIComponent(lessonId)}?ticket=${encodeURIComponent(tk.ticket)}`);
    if (!res.ok) return null;
    const data = (await res.json()) as { session: SessionCard | null };
    return data.session;
  } catch {
    return null;
  }
}

// The READ-ONLY summary card: insights · key points · your words, polished. The single CTA
// types the plan (path='dialogue'); there is deliberately no copy/insert affordance.
function renderCard(app: HTMLElement, lessonId: string, card: SessionCard, allowTalkAgain: boolean): void {
  const list = (items: string[]) => items.map((i) => `<li style="margin-bottom:8px">${esc(i)}</li>`).join("");
  const decidedCells = CELLS.filter((c) => card.decisions[c]);
  const decided = decidedCells.length
    ? `<div class="decided">
         <div class="decided-title">${t("speak.you_decided")}</div>
         ${decidedCells
           .map(
             (c) =>
               `<div class="decided-chip"><span class="cell">${cellLabel(c)}</span><span class="what">${esc(card.decisions[c]!)}</span></div>`,
           )
           .join("")}
       </div>`
    : "";

  app.innerHTML = `
    <div class="topbar"><a data-link href="/this-week" class="pill-btn">${t("common.back_this_week")}</a></div>
    <h1>${t("speak.summary_title")}</h1>
    <p class="hint">${t("speak.summary_hint")}</p>
    ${decided}
    ${
      card.summary
        ? `
      <h2>${t("speak.insights")}</h2>
      <ul style="padding-left:20px">${list(card.summary.insights)}</ul>
      <h2>${t("speak.key_points")}</h2>
      <ul style="padding-left:20px">${list(card.summary.key_points)}</ul>`
        : `<p class="empty">${t("speak.summary_pending")}</p>`
    }
    ${
      card.corrected_script
        ? `
      <h2>${t("speak.your_words")}</h2>
      <div class="card"><p style="white-space:pre-wrap;margin:0">${esc(card.corrected_script)}</p></div>
      <button class="btn secondary" id="asklead" type="button"${card.shared_with_lead ? " disabled" : ""}>
        ${card.shared_with_lead ? t("speak.asked_lead") : t("speak.ask_lead")}
      </button>
      <div style="height:8px"></div>`
        : ""
    }
    <button class="btn" id="typeplan" type="button">${t("speak.now_type")}</button>
    ${allowTalkAgain ? `<div style="height:8px"></div><button class="btn secondary" id="again" type="button">${t("speak.talk_again")}</button>` : ""}`;

  app.querySelector<HTMLButtonElement>("#typeplan")!.addEventListener("click", () => navigate(`/lesson/${lessonId}/edit?path=dialogue`));
  app.querySelector<HTMLButtonElement>("#again")?.addEventListener("click", () => void renderSession(app, lessonId));
  app.querySelector<HTMLButtonElement>("#asklead")?.addEventListener("click", async () => {
    const btn = app.querySelector<HTMLButtonElement>("#asklead")!;
    try {
      const tk = await mintTicket(lessonId);
      const res = await fetch(`${tk.dialogue_url}/session/${encodeURIComponent(card.id)}/flag?ticket=${encodeURIComponent(tk.ticket)}`, { method: "POST" });
      if (res.ok) {
        btn.textContent = t("speak.asked_lead");
        btn.disabled = true;
      }
    } catch {
      /* quiet — the button simply stays tappable */
    }
  });
}
